// Документът на прехвърляне отвсякъде (началният екран, справката, toast-ът
// след запис): модалът, анулирането с причина и редакцията. Редовете на
// списъка носят и редовете на документа, затова модалът се отваря направо.
// Връща `open`, `annul` (от менюто „⋯" на реда) и модалите за рендер.
import { useState } from "react";
import { App } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TransferDocument } from "../../../../api/inventoryTransfers";
import { useAnnulTransfer } from "../../../../hooks/useInventoryTransfers";
import { AnnulDocumentModal } from "../../components/AnnulDocumentModal";
import {
  annulUndoNote,
  productLineItems,
  useProductById,
} from "../../components/confirm/confirmPreviews";
import { useActionToast } from "../../components/useActionToast";
import { TransferDocumentModal } from "./TransferDocumentModal";

type OpenOptions = { editable?: boolean; autoPrint?: boolean };

export const useTransferDocument = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const showToast = useActionToast();
  const annulMutation = useAnnulTransfer();
  const productById = useProductById();
  const [doc, setDoc] = useState<{
    transfer: TransferDocument;
    editable: boolean;
    autoPrint: boolean;
  } | null>(null);
  const [annul, setAnnul] = useState<TransferDocument | null>(null);

  const open = (transfer: TransferDocument, options?: OpenOptions) =>
    setDoc({
      transfer,
      editable: options?.editable ?? true,
      autoPrint: options?.autoPrint ?? false,
    });

  // Редакцията получава записа през router state (без втора заявка); при
  // директен адрес листът го чете по id.
  const edit = (transfer: TransferDocument) => {
    setDoc(null);
    navigate(`/inventory/transfer/${transfer.id}/edit`, { state: { transfer } });
  };

  const confirmAnnul = (reason: string | null) => {
    if (!annul) return;
    annulMutation.mutate(
      { id: annul.id, reason },
      {
        onSuccess: () => {
          showToast({ text: t("inventoryPage.annulledToast", { number: annul.id }) });
          setAnnul(null);
          setDoc(null);
        },
        onError: (error: unknown) => {
          const failure = error as { response?: { data?: { detail?: string } } };
          message.error(failure.response?.data?.detail || t("inventoryPage.errDelete"));
        },
      },
    );
  };

  const modals = (
    <>
      <TransferDocumentModal
        open={!!doc}
        transfer={doc?.transfer ?? null}
        autoPrint={doc?.autoPrint}
        onClose={() => setDoc(null)}
        onEdit={doc?.editable ? () => edit(doc.transfer) : undefined}
        onAnnul={doc?.editable ? () => setAnnul(doc.transfer) : undefined}
      />
      <AnnulDocumentModal
        open={!!annul}
        title={t("inventoryPage.cfmAnnulTransferTitle", { number: annul?.id ?? "" })}
        text={t("inventoryPage.trxAnnulText")}
        confirmLabel={t("inventoryPage.cfmAnnulTransferConfirm")}
        preview={
          annul
            ? {
                caption: t("inventoryPage.cfmAnnulTransferCaption", {
                  warehouse: annul.fromWarehouseName ?? "",
                }),
                items: productLineItems(annul.lines ?? [], productById),
              }
            : undefined
        }
        note={annulUndoNote(t)}
        saving={annulMutation.isPending}
        onCancel={() => setAnnul(null)}
        onConfirm={confirmAnnul}
      />
    </>
  );

  return { open, edit, annul: setAnnul, modals };
};
